import { createSelector, createSlice } from '@reduxjs/toolkit';

import {
  ConnectStatus,
  type User,
} from '@wix/ambassador-adsense-v1-adsense-user/types';
import { type RootState } from '@app/settings-store';
import type { FetchStatus } from '../../types';
import {
  fetchToggleConnectedAdUnit,
  fetchUserConnectStatus,
} from './fetch-actions';

type AdsenseUserState = {
  user?: User;
  status: {
    fetchUpdateUser: FetchStatus;
    fetchUserConnectStatus: FetchStatus;
  };
};

const adsenseUserState: AdsenseUserState = {
  user: {},
  status: {
    fetchUpdateUser: 'idle',
    fetchUserConnectStatus: 'idle',
  },
};

export const adsenseUserSlice = createSlice({
  name: 'adsenseUser',
  initialState: adsenseUserState,
  reducers: {
    setAdsenseUserDisconnected: () => adsenseUserState,
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchUserConnectStatus.pending, (state) => {
        state.status.fetchUserConnectStatus = 'loading';
      })
      .addCase(fetchUserConnectStatus.fulfilled, (state, action) => {
        state.user = action.payload.adsenseUser?.user;
        state.status.fetchUserConnectStatus = 'success';
      })
      .addCase(fetchUserConnectStatus.rejected, (state) => {
        state.status.fetchUserConnectStatus = 'error';
      })
      .addCase(fetchToggleConnectedAdUnit.pending, (state) => {
        state.status.fetchUpdateUser = 'loading';
      })
      .addCase(fetchToggleConnectedAdUnit.fulfilled, (state, action) => {
        state.user = action.payload.adsenseUser?.user;
        state.status.fetchUpdateUser = 'success';
      })
      .addCase(fetchToggleConnectedAdUnit.rejected, (state) => {
        state.status.fetchUpdateUser = 'error';
      });
  },
});

export const selectAdsenseUserState = (state: RootState) => state.adsenseUser;

export const selectConnectStatusLoadStatus = createSelector(
  [selectAdsenseUserState],
  (slice) => slice.status.fetchUserConnectStatus,
);

export const selectAdsenseUser = createSelector(
  [selectAdsenseUserState],
  (state) => state.user,
);

export const selectConnectStatus = createSelector(
  [selectAdsenseUser],
  (user) => user?.adsenseInfo?.connectStatus ?? ConnectStatus.DISCONNECTED,
);

export const selectPubId = createSelector(
  [selectAdsenseUser],
  (user) => user?.adsenseInfo?.pubId ?? '',
);

export const selectUserConnectedAdunits = createSelector(
  [selectAdsenseUser],
  (user) => user?.adsenseInfo?.connectedAdunits ?? {},
);

/**
 * @returns reportingDimensionId
 */
export const selectAdUnitId = createSelector(
  [selectUserConnectedAdunits, (_, compId: string) => compId],
  (connectedAdunits, compId) => {
    for (const [key, compIdObj] of Object.entries(connectedAdunits)) {
      if (compIdObj.compId?.includes(compId)) {
        return key;
      }
    }
    return undefined;
  },
);

export const { setAdsenseUserDisconnected } = adsenseUserSlice.actions;

export default adsenseUserSlice.reducer;
export * from './fetch-actions';
